import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ImagePlus, Lock, MapPin, Plus, Trash2, UserCircle2, Users, Volume2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { VoiceSelect } from './VoiceLibrary';
import { ActorRoster, VoicePlay } from './ActorRoster';
import { CineCombo } from './CineCombo';
import { AccentSelect, accentLabel } from './LanguageAccentCard';
import { AssetPicker } from './assets/AssetPicker';
import { absUrl, addKitAsset, castActor, errMsg, listActors, patchKitAsset, removeKitAsset } from '../../lib/aiStudioApi';

const GENDER = { F: 'Female', M: 'Male' };

function ActorLine({ project, asset, roster, onChanged }) {
  const v = asset.persona || {};
  const used = new Set((project.show_kit || []).filter((k) => k.id !== asset.id).map((k) => k.actor_id));
  const options = roster.filter((a) => a.gender === asset.gender && !used.has(a.id));
  const recast = async (actorId) => {
    if (!actorId || actorId === asset.actor_id) return;
    try { const r = await castActor(project.id, asset.id, actorId); toast.success(`${r.actor.name} now plays ${asset.name} — their lines will be re-voiced`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-1.5 pt-1" data-testid={`kit-actor-${asset.id}`}>
      <div className="flex items-center gap-2 text-[11px] text-neutral-300">
        <span className="rounded-full bg-neutral-800 px-2 py-0.5 text-[10px]" data-testid={`kit-gender-${asset.id}`}>{GENDER[asset.gender] || '—'} · {asset.age}</span>
        <span className="truncate" data-testid={`kit-voice-name-${asset.id}`}>Voice: <span className="text-neutral-100">{v.eleven_name}</span> · {v.accent}, {v.tone}</span>
        {v.eleven_id && <VoicePlay voiceId={v.eleven_id} testId={`kit-voice-play-${asset.id}`} />}
      </div>
      {options.length > 1 && (
        <select value={asset.actor_id} onChange={(e) => recast(e.target.value)} data-testid={`kit-recast-${asset.id}`}
          className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-1 text-[11px] text-neutral-300">
          {options.map((a) => <option key={a.id} value={a.id}>{a.id === asset.actor_id ? `Played by ${a.name}` : `Recast: ${a.name} (${a.voice?.eleven_name})`}</option>)}
        </select>
      )}
    </div>
  );
}

const refFolders = (kind) => (kind === 'character' ? ['characters', 'actors'] : ['locations']);

/** Reference image from the Asset Library (upload lands there too) — locks this face / place in every shot. */
function KitRef({ project, asset, onChanged }) {
  const [open, setOpen] = useState(false);
  const ref = absUrl(asset.reference_urls?.[0]);
  const Icon = asset.kind === 'character' ? UserCircle2 : MapPin;
  const set = async (a) => {
    try { await patchKitAsset(project.id, asset.id, { reference_urls: [a.url] }); toast.success(`${asset.name} now matches "${a.name}" in every shot`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="Pick or upload a reference image" data-testid={`kit-ref-${asset.id}`}
        className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900 transition-colors duration-200 hover:border-amber-400">
        {ref ? <img src={ref} alt={asset.name} className="h-full w-full object-cover" />
          : <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-neutral-500"><Icon size={20} /><span className="px-1 text-center text-[9px]">ref on Produce</span></div>}
        <span className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-1 bg-black/70 py-0.5 text-[9px] text-amber-200 opacity-0 transition-opacity group-hover:opacity-100"><ImagePlus size={10} /> Library</span>
      </button>
      <AssetPicker open={open} onOpenChange={setOpen} folders={refFolders(asset.kind)} onPick={set} title={`Reference image for ${asset.name}`} />
    </>
  );
}

function VoiceAccent({ project, catalog, asset, onChanged }) {
  const patch = async (p, msg) => {
    try { await patchKitAsset(project.id, asset.id, p); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const languages = catalog.scene?.languages || [];
  const fallback = accentLabel(catalog, asset.actor_accent || project.accent || 'actor').split(' — ')[0].split(' (')[0];
  return (
    <div className="grid grid-cols-2 gap-2 pt-1" data-testid={`kit-voice-accent-${asset.id}`}>
      <CineCombo label="Speaks" value={asset.language || ''} options={languages} allowCustom placeholder={project.language || 'English'} clearLabel="Same as the production"
        onChange={(v) => patch({ language: v }, v ? `${asset.name} now speaks ${v} — lines re-voice on the next Produce` : `${asset.name} speaks ${project.language || 'English'}`)}
        testId={`kit-language-${asset.id}`} />
      <AccentSelect catalog={catalog} value={asset.accent || ''} inherit={`Default · ${fallback || 'production'}`} label="Accent"
        onChange={(v) => patch({ accent: v }, `${asset.name}'s accent updated — lines re-voice on the next Produce (free when cached)`)} testId={`kit-accent-${asset.id}`} />
    </div>
  );
}

function KitCard({ project, catalog, asset, voices, roster, onChanged }) {
  const setVoice = async (voice_id) => {
    try { await patchKitAsset(project.id, asset.id, { voice_id }); toast.success(`${asset.name}'s voice set`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-3 rounded-xl border border-neutral-800 bg-neutral-950/60 p-3" data-testid={`kit-card-${asset.id}`}>
      <KitRef project={project} asset={asset} onChanged={onChanged} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 truncate text-sm font-semibold text-neutral-100"><Lock size={11} className="text-amber-300" /> {asset.name}</div>
          <button type="button" onClick={() => removeKitAsset(project.id, asset.id).then(onChanged)} className="text-neutral-600 hover:text-red-300" data-testid={`kit-remove-${asset.id}`}><Trash2 size={13} /></button>
        </div>
        <p className="line-clamp-2 text-[11px] text-neutral-400">{asset.appearance || asset.description}</p>
        {asset.wardrobe && <p className="line-clamp-1 text-[11px] text-neutral-500">Wardrobe: {asset.wardrobe}</p>}
        {asset.kind === 'character' && (asset.actor_id ? <ActorLine project={project} asset={asset} roster={roster} onChanged={onChanged} />
          : <div className="pt-1"><VoiceSelect voices={voices} value={asset.voice_id} onChange={setVoice} testId={`kit-voice-${asset.id}`} placeholder="Cast a voice…" /></div>)}
        {asset.kind === 'character' && project.kind === 'movie' && <VoiceAccent project={project} catalog={catalog} asset={asset} onChanged={onChanged} />}
        {asset.kind === 'location' && asset.ambient_url && (
          <div className="flex items-center gap-2 pt-1 text-[11px] text-neutral-400" data-testid={`kit-ambience-${asset.id}`}>
            <Volume2 size={11} className="text-emerald-300" /> Ambience · {asset.ambient_engine === 'elevenlabs_kie' ? 'ElevenLabs' : 'room tone (free)'}
            <audio src={absUrl(asset.ambient_url)} controls className="h-6 w-32" data-testid={`kit-ambience-audio-${asset.id}`} />
          </div>
        )}
      </div>
    </div>
  );
}

function AddAsset({ project, onChanged }) {
  const [form, setForm] = useState({ kind: 'character', name: '', appearance: '', wardrobe: '', ref: null });
  const [picking, setPicking] = useState(false);
  async function add() {
    if (!form.name.trim()) { toast.error('Name the character or location'); return; }
    try {
      await addKitAsset(project.id, {
        kind: form.kind, name: form.name.trim(), appearance: form.appearance.trim(), description: form.appearance.trim(),
        wardrobe: form.wardrobe.trim(), reference_urls: form.ref ? [form.ref.url] : [],
      });
      setForm({ ...form, name: '', appearance: '', wardrobe: '', ref: null });
      toast.success('Locked into the Show Kit'); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  }
  return (
    <div className="rounded-xl border border-dashed border-neutral-800 p-3">
      <div className="mb-2 text-[11px] uppercase tracking-wider text-neutral-500">Add to Show Kit</div>
      <div className="grid gap-2 md:grid-cols-[130px_1fr_2fr]">
        <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="kit-kind-select"
          className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs"><option value="character">Character</option><option value="location">Location</option></select>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="border-neutral-800 bg-neutral-950 text-sm" data-testid="kit-name-input" />
        <Input value={form.appearance} onChange={(e) => setForm({ ...form, appearance: e.target.value })} placeholder={form.kind === 'character' ? 'Look: face, hair, build, age' : 'Visual description'} className="border-neutral-800 bg-neutral-950 text-sm" data-testid="kit-appearance-input" />
        {form.kind === 'character' && <Input value={form.wardrobe} onChange={(e) => setForm({ ...form, wardrobe: e.target.value })} placeholder="Wardrobe" className="border-neutral-800 bg-neutral-950 text-sm md:col-start-2" data-testid="kit-wardrobe-input" />}
        <div className="flex items-center gap-2 md:col-start-3" data-testid="kit-ref-picker">
          {form.ref ? (
            <span className="flex min-w-0 items-center gap-2 rounded-md border border-amber-400/40 bg-amber-500/10 px-2 py-1">
              <img src={absUrl(form.ref.url)} alt="" className="h-7 w-7 rounded object-cover" />
              <span className="truncate text-xs text-amber-100">{form.ref.name}</span>
              <button type="button" onClick={() => setForm({ ...form, ref: null })} className="text-neutral-400 hover:text-red-300" data-testid="kit-ref-clear"><X size={12} /></button>
            </span>
          ) : (
            <Button type="button" size="sm" variant="outline" onClick={() => setPicking(true)} className="border-neutral-700 text-neutral-200" data-testid="kit-ref-pick-btn">
              <ImagePlus size={13} className="mr-1" /> Reference image — upload or pick (optional)
            </Button>
          )}
        </div>
      </div>
      <AssetPicker open={picking} onOpenChange={setPicking} folders={refFolders(form.kind)} onPick={(a) => setForm((f) => ({ ...f, ref: a, name: f.name || a.name }))}
        title="Reference image — upload once, reuse in any production" />
      <div className="mt-2 flex justify-end"><Button size="sm" onClick={add} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="kit-add-btn"><Plus size={13} className="mr-1" /> Lock asset</Button></div>
    </div>
  );
}

export function ShowKitPanel({ project, catalog, voices, onChanged }) {
  const [roster, setRoster] = useState([]);
  const [open, setOpen] = useState(false);
  const kit = project.show_kit || [];
  const loadRoster = () => listActors().then((d) => setRoster(d.actors)).catch(() => {});
  useEffect(() => { loadRoster(); }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = () => { loadRoster(); onChanged(); };
  return (
    <div className="space-y-5" data-testid="show-kit-panel">
      {[['character', 'Cast'], ['location', 'Locations']].map(([kind, title]) => {
        const items = kit.filter((a) => a.kind === kind);
        return (
          <div key={kind}>
            <div className="mb-2 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-neutral-400">{title} ({items.length}/8) · {kind === 'character' ? 'real actors — same face & voice everywhere' : 'one ambience loop each'}</div>
              {kind === 'character' && (
                <button type="button" onClick={() => setOpen(true)} data-testid="open-actor-roster"
                  className="flex items-center gap-1.5 rounded-full border border-neutral-800 px-3 py-1 text-[11px] text-neutral-300 transition-colors duration-200 hover:border-amber-400 hover:text-amber-200">
                  <Users size={12} /> Actor roster ({roster.length})
                </button>
              )}
            </div>
            {items.length ? <div className="grid gap-3 md:grid-cols-2">{items.map((a) => <KitCard key={a.id} project={project} catalog={catalog} asset={a} voices={voices} roster={roster} onChanged={changed} />)}</div>
              : <div className="text-xs text-neutral-500">None yet — the AI Director creates these automatically, or add them below.</div>}
          </div>
        );
      })}
      <AddAsset project={project} onChanged={onChanged} />
      <ActorRoster open={open} onOpenChange={setOpen} onChanged={onChanged} />
    </div>
  );
}
